import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { FormProvider, useForm } from 'react-hook-form'
import { Dialog, DialogContent, Box, Card, CardContent, Typography, Divider, DialogActions } from '@mui/material'
import { useNotification } from '@/hooks/useNotification'
import { getChangePhoneSchema, User } from '@/@data'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslator } from '@/hooks/useTranslator'
import { Button } from '@/@core/components/mui/Button'
// import {  IUser, } from '@/@data'

interface EmployeeConfirmPhoneDialogProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  employee: any
  newPhone?: any
  refetch?: () => void
}

export const EmployeeConfirmPhoneDialog = ({
  open,
  onClose,
  employee,
  newPhone,
  onSuccess,
  refetch
}: EmployeeConfirmPhoneDialogProps) => {
  const { __ } = useTranslator()
  const n = useNotification()

  if (!employee) return null

  const methods = useForm<any>({
    defaultValues: {
      phone_code: employee?.phone_code || '+84',
      phone: employee?.phone || '',
      phone_new: newPhone
    },
    resolver: yupResolver(getChangePhoneSchema(__)),
    mode: 'onChange'
  })

  // Keep form values in sync if props change
  useEffect(() => {
    methods.reset({
      phone_code: employee?.phone_code || '+84',
      phone: employee?.phone || '',
      phone_new: newPhone || ''
    })
  }, [employee, newPhone, methods])

  const handleSubmit = async (data: any) => {
    if (!employee) return

    try {
      // Construct payload with status = active
      const payload = {
        ...data,
        phone: data.phone_new
      }

      delete payload.phone_new

      await User.updateEmployee(employee.id, payload)
      n.success(__('User updated successfully'))
      onSuccess?.()
      if (refetch) refetch()
      onClose()
    } catch (error: any) {
      if (error.status === 422) {
        const capitalize = (str: string) => str && str[0].toUpperCase() + str.slice(1)
        const messsage = capitalize(error.message[0].name) + error.message[0].errors.join('')

        n.error(__(messsage))

        return
      }

      n.error(__('Failed to update user'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      scroll='body'
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '1270px', borderRadius: '32px' }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogContent className='p-0 m-0' sx={{ padding: 0, borderRadius: '32px' }}>
            <DialogCloseButton onClick={onClose} disableRipple>
              <i className='tabler-x' />
            </DialogCloseButton>
            <Card>
              <CardContent>
                <Typography variant='h5' className='text-[#2F2B3DE5] mb-6'>
                  {__('Change Employee Phone')}
                </Typography>

                <Box className='flex items-center justify-center gap-6'>
                  {/* From card */}
                  <Box className='flex-1 rounded-2xl bg-[#3551F214] p-6'>
                    <Typography variant='h6' className='text-[#2F2B3DE5]'>
                      {__('Change Employee Phone From')}
                    </Typography>
                    <Divider className='!my-4' />
                    <Box className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employee Name')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.first_name || employee?.last_name
                            ? `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim()
                            : '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employer')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.company?.name || employee?.company_name || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employee Code')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.employee_id || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Phone')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.phone_code} {employee?.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Link icon */}
                  <Box className='hidden md:flex items-center justify-center'>
                    <i className='tabler-link text-[28px] text-[#4B5BFF]' />
                  </Box>

                  {/* To card */}
                  <Box className='flex-1 rounded-2xl bg-[#3551F214] p-6'>
                    <Typography variant='h6' className='text-[#2F2B3DE5]'>
                      {__('Change Employee Phone To')}
                    </Typography>
                    <Divider className='!my-4' />
                    <Box className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employee Name')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.first_name || employee?.last_name
                            ? `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim()
                            : '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employer')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.company?.name || employee?.company_name || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Employee Code')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.employee_id || '-'}
                        </Typography>
                      </Box>

                      <Box className='flex flex-col'>
                        <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                          {__('Phone')}
                        </Typography>
                        <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                          {employee?.phone_code} {newPhone}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </DialogContent>

          <DialogActions className='px-[20px] sm:px-[24px] text-center justify-end gap-2'>
            <Button variant='outlined' onClick={onClose} startIcon={<i className='tabler-arrow-left' />}>
              {__('Previous')}
            </Button>
            <Button variant='contained' type='submit' startIcon={<i className='tabler-phone-check' />} color='success'>
              {__('Confirm Change Employee Phone')}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
